import { Link } from '@tanstack/react-router'
import { PexIcon, type PexIconProps } from '../../../components/icons/PexIcon'
import { Button, buttonVariants } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { PageHeader } from '../../../components/ui/page-header'
import { useT } from '../../../i18n'
import { SELLER_HOME_T } from '../../../i18n/seller/home'
import { formatCurrency } from '../../../lib/currency'
import { cn } from '../../../lib/utils'
import type { SellerListingsResponse } from '../../../api/seller-listings'
import { SellerCatalogProductCard } from '../catalog/SellerCatalogProductCard'
import type { CatalogProduct } from '../catalog/catalog-types'
import type { SellerHomeSummary, SellerStoreHealthResponse } from './seller-home-api'
import {
  deriveSellerMaturity,
  rankSellerHomeActions,
  type SellerHomeActionKind,
} from './seller-home-model'

export type SellerHomeModule<T> = {
  status: 'loading' | 'ready' | 'error' | 'restricted'
  data?: T
  retry?: () => void
}

export type SellerHomeViewProps = {
  summary: SellerHomeModule<SellerHomeSummary>
  health: SellerHomeModule<SellerStoreHealthResponse>
  listings: SellerHomeModule<SellerListingsResponse>
  catalogue: SellerHomeModule<CatalogProduct[]>
  onToggleFavorite: (productId: string) => void
}

const ACTION_ICON: Record<SellerHomeActionKind, PexIconProps['name']> = {
  'store-failing': 'integration',
  'seller-approval': 'orders',
  'ready-for-shipping': 'fulfillment',
  'listing-issues': 'warning',
  'connect-store': 'link',
  'create-listing': 'products',
}

function SectionHeading({ title, hint, id }: { title: string; hint: string; id: string }) {
  return (
    <div>
      <h2 id={id} className="text-lg font-semibold tracking-[-0.025em] text-[var(--color-text)]">
        {title}
      </h2>
      <p className="mt-1 text-sm text-[var(--color-text-muted)]">{hint}</p>
    </div>
  )
}

function ModuleUnavailable({ retry }: { retry?: () => void }) {
  const t = useT(SELLER_HOME_T)
  return (
    <div className="flex min-h-24 items-center justify-between gap-4 rounded-[var(--radius-control)] bg-[var(--color-surface-muted)] p-4">
      <span className="text-sm text-[var(--color-text-muted)]">{t.unavailable}</span>
      {retry ? <Button variant="outline" size="sm" onClick={retry}>{t.retry}</Button> : null}
    </div>
  )
}

function MetricCard({
  label,
  value,
  qualifier,
  icon,
  loading,
}: {
  label: string
  value: string
  qualifier: string
  icon: PexIconProps['name']
  loading: boolean
}) {
  return (
    <Card className="min-w-0 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-text-muted)]">{label}</p>
          {loading ? (
            <div className="mt-3 h-8 w-24 animate-pulse rounded-[var(--radius-control)] bg-[var(--color-surface-muted)]" />
          ) : (
            <p className="mt-2 text-[1.75rem] font-semibold leading-none tracking-[-0.04em] tabular-nums text-[var(--color-text)]">{value}</p>
          )}
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">{qualifier}</p>
        </div>
        <span className="flex size-9 items-center justify-center rounded-[var(--radius-control)] bg-[var(--color-surface-muted)]">
          <PexIcon name={icon} size="sm" tone="muted" decorative />
        </span>
      </div>
    </Card>
  )
}

function SalesChart({ summary }: { summary: SellerHomeSummary }) {
  const t = useT(SELLER_HOME_T)
  const points = summary.orders.daily
  const max = Math.max(...points.map((point) => point.gross_sales_usd), 1)
  const polyline = points
    .map((point, index) => {
      const x = points.length === 1 ? 0 : (index / (points.length - 1)) * 100
      const y = 54 - (point.gross_sales_usd / max) * 48
      return `${x},${y}`
    })
    .join(' ')
  const gross = formatCurrency(summary.orders.gross_sales_usd, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[1.75rem] font-semibold tracking-[-0.04em] text-[var(--color-text)]">{gross}</p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">{t.ordersCount(summary.orders.total)} · {t.metrics.last30Days}</p>
        </div>
        {summary.orders.previous_gross_sales_usd > 0 ? (
          <span className="text-xs font-semibold text-[var(--color-success)]">
            {t.vsPreviousPeriod(Math.round(((summary.orders.gross_sales_usd - summary.orders.previous_gross_sales_usd) / summary.orders.previous_gross_sales_usd) * 100))}
          </span>
        ) : null}
      </div>
      {summary.orders.total === 0 ? (
        <div className="mt-6 rounded-[var(--radius-control)] bg-[var(--color-surface-muted)] p-5">
          <p className="text-sm font-semibold text-[var(--color-text)]">{t.noSalesTitle}</p>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.noSalesBody}</p>
        </div>
      ) : (
        <div className="mt-5 h-36 w-full overflow-hidden" aria-hidden="true">
          <svg viewBox="0 0 100 58" preserveAspectRatio="none" className="h-full w-full text-[var(--color-signal)]">
            <path d="M0 55 H100" stroke="var(--color-border)" strokeWidth="0.5" />
            <polyline points={polyline} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      )}
      <p className="sr-only">{t.chartSummary(summary.orders.total, gross)}</p>
      <div className="sr-only" data-testid="sales-performance-data-table">
        <table>
          <caption>{t.sections.performance}</caption>
          <thead><tr><th>{t.table.date}</th><th>{t.table.orders}</th><th>{t.table.grossSales}</th></tr></thead>
          <tbody>{points.map((point) => <tr key={point.date}><td>{point.date}</td><td>{point.order_count}</td><td>{formatCurrency(point.gross_sales_usd)}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  )
}

export function SellerHomeView({ summary, health, listings, catalogue, onToggleFavorite }: SellerHomeViewProps) {
  const t = useT(SELLER_HOME_T)
  const activeStores = health.status === 'ready'
    ? health.data?.items.filter((store) => store.active).length ?? 0
    : null
  const failingStores = health.status === 'ready'
    ? health.data?.items.filter((store) => store.verdict !== 'healthy').length ?? 0
    : null
  const activeListings = listings.status === 'ready' ? listings.data?.meta.facets.live ?? 0 : null
  const listingIssues = listings.status === 'ready' ? listings.data?.meta.facets.issues ?? 0 : null
  const orderTotal = summary.status === 'ready' ? summary.data?.orders.total ?? 0 : null
  const orderByState = summary.status === 'ready' ? summary.data?.orders.by_state ?? {} : null
  const snapshot = { activeStores, failingStores, activeListings, listingIssues, orderTotal, orderByState }
  const maturity = deriveSellerMaturity(snapshot)
  const actions = rankSellerHomeActions(snapshot).slice(0, 2)
  const summaryLoading = summary.status === 'loading'
  const listingsLoading = listings.status === 'loading'

  return (
    <div
      className="mx-auto w-full max-w-[112rem] space-y-8"
      data-testid="seller-home"
      data-tour="seller-overview"
    >
      <div className="space-y-2">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">{t.eyebrow}</p>
        <PageHeader
          title={t.title}
          description={t.description}
          actions={(
            <>
              <Link to="/seller/my-orders" className={buttonVariants({ variant: 'outline' })}>{t.viewOrders}</Link>
              <Link to="/seller/catalog" className={buttonVariants({ variant: 'primary' })}><PexIcon name="add" size="xs" tone="on-emphasis" decorative />{t.createProduct}</Link>
            </>
          )}
        />
      </div>

      <section aria-labelledby="seller-home-focus">
        <Card className="overflow-hidden">
          <div className="grid lg:grid-cols-[1.05fr_1fr_1fr]">
            <div className="border-b border-[var(--color-border)] p-5 lg:border-b-0 lg:border-r">
              <SectionHeading id="seller-home-focus" title={t.sections.focus} hint={t.hints.focus} />
            </div>
            {actions.length === 0 ? (
              <div className="flex items-center gap-4 p-5 lg:col-span-2">
                <span className="flex size-10 items-center justify-center rounded-[var(--radius-control)] bg-[color-mix(in_srgb,var(--color-success)_10%,transparent)]">
                  <PexIcon name="success" size="sm" tone="success" decorative />
                </span>
                <div><h3 className="text-sm font-semibold text-[var(--color-text)]">{t.focusHealthyTitle}</h3><p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.focusHealthyBody}</p></div>
              </div>
            ) : actions.map((action, index) => {
              const copy = t.action[action.kind]
              return (
                <div key={action.kind} className={cn('flex flex-col justify-between gap-5 p-5', index > 0 && 'border-t border-[var(--color-border)] sm:border-t-0 sm:border-l')}>
                  <div className="flex items-start gap-3">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[var(--color-surface-muted)]"><PexIcon name={ACTION_ICON[action.kind]} size="sm" tone={action.kind === 'store-failing' ? 'danger' : 'signal'} decorative /></span>
                    <div><h3 className="text-sm font-semibold text-[var(--color-text)]">{copy.title(action.count)}</h3><p className="mt-1 text-sm leading-5 text-[var(--color-text-muted)]">{copy.body}</p></div>
                  </div>
                  <Link to={action.to} className="text-sm font-semibold text-[var(--color-text)] underline-offset-4 hover:underline">{copy.cta} →</Link>
                </div>
              )
            })}
          </div>
        </Card>
      </section>

      <section aria-labelledby="seller-home-pulse" className="space-y-3">
        <h2 id="seller-home-pulse" className="sr-only">{t.sections.pulse}</h2>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard label={t.metrics.grossSales} value={summary.status === 'ready' ? formatCurrency(summary.data?.orders.gross_sales_usd ?? 0, 'USD', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : t.unavailable} qualifier={t.metrics.last30Days} icon="monetization" loading={summaryLoading} />
          <MetricCard label={t.metrics.orders} value={summary.status === 'ready' ? String(orderTotal ?? 0) : t.unavailable} qualifier={t.metrics.last30Days} icon="orders" loading={summaryLoading} />
          <MetricCard label={t.metrics.readyToShip} value={summary.status === 'ready' ? String(orderByState?.ready_for_shipping ?? 0) : t.unavailable} qualifier={t.metrics.last30Days} icon="fulfillment" loading={summaryLoading} />
          <MetricCard label={t.metrics.activeListings} value={listings.status === 'ready' ? String(activeListings ?? 0) : t.unavailable} qualifier={listings.status === 'ready' ? t.metrics.listingIssues(listingIssues ?? 0) : t.unavailable} icon="navListings" loading={listingsLoading} />
        </div>
      </section>

      <section aria-labelledby="seller-home-growth" className="space-y-4">
        <SectionHeading id="seller-home-growth" title={t.sections.growth} hint={t.hints.growth} />
        <Card className="grid overflow-hidden md:grid-cols-3">
          {t.growth[maturity].map((step, index) => {
            const completed = maturity === 'established' ? index === 0 : maturity === 'connected-empty' ? index === 0 : false
            const active = !completed && (maturity === 'new' ? index === 0 : index === 1)
            return (
              <div key={step.title} className={cn('relative p-5', index > 0 && 'border-t border-[var(--color-border)] md:border-l md:border-t-0', active && 'bg-[color-mix(in_srgb,var(--color-signal)_5%,var(--color-surface))]')}>
                {active ? <span className="absolute inset-y-0 left-0 w-0.5 bg-[var(--color-signal)]" /> : null}
                <div className="flex items-center gap-2"><span className="flex size-7 items-center justify-center rounded-[var(--radius-control)] bg-[var(--color-surface-muted)] text-xs font-semibold text-[var(--color-text)]">{completed ? <PexIcon name="success" size="xs" tone="success" decorative /> : index + 1}</span><span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-muted)]">{completed ? t.completed : active ? t.next : `0${index + 1}`}</span></div>
                <h3 className="mt-4 text-sm font-semibold text-[var(--color-text)]">{step.title}</h3>
                <p className="mt-1 text-sm leading-5 text-[var(--color-text-muted)]">{step.body}</p>
              </div>
            )
          })}
        </Card>
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]" data-testid="seller-home-performance-health">
        <Card className="p-5 sm:p-6" aria-labelledby="seller-home-performance">
          <div className="flex items-start justify-between gap-4"><SectionHeading id="seller-home-performance" title={t.sections.performance} hint={t.hints.performance} /><Link to="/seller/multi-store-report" className="shrink-0 text-xs font-semibold text-[var(--color-text)] hover:underline">{t.viewReport} →</Link></div>
          <div className="mt-6">{summary.status === 'ready' && summary.data ? <SalesChart summary={summary.data} /> : summary.status === 'loading' ? <div className="h-48 animate-pulse rounded-[var(--radius-control)] bg-[var(--color-surface-muted)]" /> : <ModuleUnavailable retry={summary.retry} />}</div>
        </Card>

        <Card className="p-5 sm:p-6" aria-labelledby="seller-home-health">
          <div className="flex items-start justify-between gap-4"><SectionHeading id="seller-home-health" title={t.sections.health} hint={t.hints.health} /><Link to="/seller/stores" className="shrink-0 text-xs font-semibold text-[var(--color-text)] hover:underline">{t.manageStores} →</Link></div>
          <div className="mt-5">{health.status === 'ready' ? health.data?.items.length ? <ul className="divide-y divide-[var(--color-border)]">{health.data.items.slice().sort((a, b) => ({ failing: 0, degraded: 1, healthy: 2 }[a.verdict] - { failing: 0, degraded: 1, healthy: 2 }[b.verdict])).slice(0, 4).map((store) => <li key={store.store_integration_id} className="flex items-start justify-between gap-3 py-3 first:pt-0"><div className="min-w-0"><p className="truncate text-sm font-semibold text-[var(--color-text)]">{store.shop_name}</p><p className="mt-1 line-clamp-1 text-xs text-[var(--color-text-muted)]">{store.reasons[0] ?? store.provider}</p></div><span className={cn('shrink-0 text-xs font-semibold', store.verdict === 'healthy' ? 'text-[var(--color-success)]' : store.verdict === 'degraded' ? 'text-[var(--color-warning)]' : 'text-[var(--color-danger)]')}>{t[store.verdict]}</span></li>)}</ul> : <p className="text-sm text-[var(--color-text-muted)]">{t.noStores}</p> : health.status === 'loading' ? <div className="h-40 animate-pulse rounded-[var(--radius-control)] bg-[var(--color-surface-muted)]" /> : <ModuleUnavailable retry={health.retry} />}</div>
        </Card>
      </section>

      {catalogue.status === 'loading' || (catalogue.status === 'ready' && (catalogue.data?.length ?? 0) > 0) ? (
        <section aria-labelledby="seller-home-catalogue" className="space-y-4">
          <div className="flex items-end justify-between gap-4"><SectionHeading id="seller-home-catalogue" title={t.sections.catalogue} hint={t.hints.catalogue} /><Link to="/seller/catalog" className="shrink-0 text-sm font-semibold text-[var(--color-text)] hover:underline">{t.viewCatalogue} →</Link></div>
          {catalogue.status === 'loading' ? <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 3 }).map((_, index) => <div key={index} className="aspect-[4/5] animate-pulse rounded-[var(--catalog-card-radius)] bg-[var(--color-surface-muted)]" />)}</div> : <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{catalogue.data?.slice(0, 3).map((product) => <SellerCatalogProductCard key={product.id} productId={product.id} routeId={product.slug} name={product.name} category={product.category} coverImageUrl={product.coverImageUrl} hoverImageUrl={product.hoverImageUrl} priceUsd={product.priceUsd} label={product.label} favorite={product.isFavorite} facts={product.cardFacts} onToggleFavorite={onToggleFavorite} />)}</div>}
        </section>
      ) : null}
    </div>
  )
}
